import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { ChevronLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const SUBGROUP_TYPES = [
  { value: 'general', label: 'General', emoji: '💬', description: 'General discussions' },
  { value: 'breed', label: 'Breed', emoji: '🐕', description: 'Breed-specific topics' },
  { value: 'topic', label: 'Topic', emoji: '📚', description: 'Topic-based discussions' },
  { value: 'location', label: 'Location', emoji: '📍', description: 'Location-based community' },
  { value: 'activity', label: 'Activity', emoji: '🏃', description: 'Activity & training' },
  { value: 'health', label: 'Health', emoji: '🏥', description: 'Health discussions' },
  { value: 'rescue', label: 'Rescue', emoji: '🏠', description: 'Rescue & adoption' },
  { value: 'marketplace', label: 'Marketplace', emoji: '🛒', description: 'Buy/Sell/Trade' },
  { value: 'events', label: 'Events', emoji: '📅', description: 'Event planning' },
  { value: 'showcase', label: 'Showcase', emoji: '⭐', description: 'Show off pets' }
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public', description: 'Anyone can view and join' },
  { value: 'members_only', label: 'Members Only', description: 'Only club members can view' },
  { value: 'private', label: 'Private', description: 'Invite only' }
];

const COLOR_OPTIONS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

const EMOJI_OPTIONS = ['🐕', '🐶', '🐩', '🐾', '🦴', '❤️', '⭐', '💬', '🏆', '📍', '🏥', '🏠', '📚', '🎉'];

const CreateSubGroup = () => {
  usePageTitle('Create Community');
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    club_id: '',
    subgroup_type: 'general',
    icon_emoji: '🐾',
    color: '#6366f1',
    visibility: 'public',
    rules: ['']
  });

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/communities/create' } });
      return;
    }
    fetchUserClubs();
  }, [user, navigate]);

  const fetchUserClubs = async () => {
    try {
      // Get clubs the user is a member of
      const response = await axios.get(`${API}/api/clubs`);
      setClubs(response.data);
      
      // Auto-select first club if only one
      if (response.data.length === 1) {
        setFormData(prev => ({ ...prev, club_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRuleChange = (index, value) => {
    const newRules = [...formData.rules];
    newRules[index] = value;
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  const addRule = () => {
    if (formData.rules.length < 10) {
      setFormData(prev => ({ ...prev, rules: [...prev.rules, ''] }));
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.club_id) {
      toast.error('Please select a club');
      return;
    }

    try {
      setLoading(true);
      
      // Filter out empty rules
      const rules = formData.rules.filter(r => r.trim());
      
      const response = await axios.post(`${API}/api/subgroups`, {
        ...formData,
        rules
      });
      
      toast.success('Community created successfully!');
      navigate(`/communities/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create community');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <button
          onClick={() => navigate('/communities')}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Communities
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Create a Community</CardTitle>
            <CardDescription>
              Start a new topic-based community within your club. 
              <span className="block mt-1 text-primary font-medium">
                Communities that grow to 50+ members can graduate to fully independent clubs!
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Club Selection */}
              <div className="space-y-2">
                <Label>Club *</Label>
                <Select
                  value={formData.club_id}
                  onValueChange={(value) => handleChange('club_id', value)}
                >
                  <SelectTrigger data-testid="club-select">
                    <SelectValue placeholder="Select a club" />
                  </SelectTrigger>
                  <SelectContent>
                    {clubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>
                        {club.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The community will be part of this club
                </p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label>Community Name *</Label>
                <Input
                  placeholder="e.g., Golden Retriever Lovers"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  minLength={3}
                  maxLength={100}
                  data-testid="community-name"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="What is this community about?"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                  minLength={10}
                  maxLength={1000}
                  rows={3}
                  data-testid="community-description"
                />
              </div>

              {/* Type */}
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.subgroup_type}
                  onValueChange={(value) => handleChange('subgroup_type', value)}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBGROUP_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <span>{type.emoji}</span>
                          <span>{type.label}</span>
                          <span className="text-xs text-muted-foreground">- {type.description}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visibility */}
              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value) => handleChange('visibility', value)}
                >
                  <SelectTrigger data-testid="visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex flex-col">
                          <span>{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.description}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Icon & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {EMOJI_OPTIONS.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleChange('icon_emoji', emoji)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl border-2 transition-all ${
                          formData.icon_emoji === emoji 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Theme Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handleChange('color', color)}
                        className={`w-8 h-8 rounded-full transition-all ${
                          formData.color === color 
                            ? 'ring-2 ring-offset-2 ring-primary' 
                            : ''
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <Label className="mb-2 block">Preview</Label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: `${formData.color}20` }}
                  >
                    {formData.icon_emoji}
                  </div>
                  <div>
                    <h4 className="font-semibold">{formData.name || 'Community Name'}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {formData.description || 'Community description...'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Rules */}
              <div className="space-y-2">
                <Label>Community Rules (optional)</Label>
                <div className="space-y-2">
                  {formData.rules.map((rule, index) => (
                    <div key={`rule-${index}-${rule.slice(0,10)}`} className="flex gap-2">
                      <span className="w-6 h-9 flex items-center justify-center text-sm text-muted-foreground">
                        {index + 1}.
                      </span>
                      <Input
                        placeholder={`Rule ${index + 1}`}
                        value={rule}
                        onChange={(e) => handleRuleChange(index, e.target.value)}
                        className="flex-1"
                      />
                      {formData.rules.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRule(index)}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}
                  {formData.rules.length < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addRule}
                      className="ml-8"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Rule
                    </Button>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/communities')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="create-community-submit">
                  {loading ? 'Creating...' : 'Create Community'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateSubGroup;
